import { DashboardMap } from "@/components/dashboard/dashboard-map";
import { DashboardMapViewLayout } from "@/components/dashboard/dashboard-map-view-layout";
import { DashboardViewToggle } from "@/components/dashboard/dashboard-view-toggle";
import { LicenseDetailsSheet } from "@/components/dashboard/license-details-sheet";
import { MapErrorBoundary } from "@/components/dashboard/map-error-boundary";
import { OmtAmsDashboardUsers } from "@/components/omt-ams/dashboard-users";
import { DashboardHeader } from "@/components/registrant/dashboard/dashboard-header";
import { DashboardTabList } from "@/components/registrant/dashboard/dashboard-tab-list";
import { RegistrantBulkRegistrationsTable } from "@/components/registrant/dashboard/registrant-bulk-registrations-table";
import ErrorComponent from "@/components/ui/error";
import LoadingScreen from "@/components/ui/loading-screen";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { UserType } from "@/constants";
import { getLicenseTypeQuestionsOptions } from "@/hera-api/client/@tanstack/react-query.gen";
import {
  getOmtAmsAccess,
  getOmtAmsLicenseComments,
  getOmtAmsLicensePayments,
  getOmtAmsLicenseStats,
} from "@/hera-api/omt-ams-api";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { useLicenseDetailsWithRuntimeRefresh } from "@/hooks/use-license-details-with-runtime-refresh";
import {
  fetchOmtAmsLicenseFilterValues,
  useOmtAmsLicenseMapPins,
  usePaginatedOmtAmsLicenses,
} from "@/hooks/use-paginated-omt-ams-licenses";
import type { RegistrantLicenseListTab } from "@/hooks/use-paginated-registrant-licenses";
import { setLicenseDisplayedSnapshot } from "@/lib/license-displayed-snapshot";
import type { MapViewport } from "@/lib/map-clusters";
import { getOmtAmsDashboardContextHelpArticleId } from "@/lib/omt-ams-help";
import { cn, getQueryErrorHeading, getQueryErrorMessage } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { SortingState } from "@tanstack/react-table";
import { useCallback, useEffect, useRef, useState } from "react";

export const Route = createFileRoute("/omt-ams/dashboard")({
  component: OmtAmsDashboard,
  validateSearch: (
    search: Record<string, unknown>,
  ): { openLicense?: string; tab?: string } => ({
    openLicense:
      typeof search.openLicense === "string" ? search.openLicense : undefined,
    tab: typeof search.tab === "string" ? search.tab : undefined,
  }),
});

function tabToApiTab(tab: string): RegistrantLicenseListTab | undefined {
  if (tab === "needs-attention") return "needs_attention";
  if (tab === "renewable") return "renewable";
  if (tab === "inactive") return "inactive";
  if (tab === "all") return "all_active";
  return undefined;
}

export function initialOmtAmsTab(tab: string | undefined): string {
  if (tab === "invoices" || tab === "balance-due") return "all";
  return tab ?? "all";
}

export function OmtAmsDashboard() {
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [isMapView, setIsMapView] = useState(false);
  const [selectedTab, setSelectedTab] = useState(initialOmtAmsTab(search.tab));
  const [openLicenseId, setOpenLicenseId] = useState<string | null>(
    search.openLicense ?? null,
  );

  useEffect(() => {
    setLicenseDisplayedSnapshot({ openLicenseId });
  }, [openLicenseId]);
  const [bulkPaymentCohort, setBulkPaymentCohort] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sorting, setSorting] = useState<SortingState>([
    { id: "last_updated_at", desc: true },
  ]);
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>(
    {},
  );
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({});
  const [facetValuesByColumn, setFacetValuesByColumn] = useState<
    Record<string, string[]>
  >({});

  const accessQuery = useQuery({
    queryKey: ["omt-ams-access"],
    queryFn: getOmtAmsAccess,
    enabled: isAuthSetupComplete,
  });

  const listTab = tabToApiTab(selectedTab);
  const sortBy = sorting[0]?.id ?? "last_updated_at";
  const sortDir = sorting[0]?.desc === false ? "asc" : "desc";

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset to first page when list filters change
  useEffect(() => {
    setPageIndex(0);
  }, [selectedTab, bulkPaymentCohort, columnFilters, sorting]);

  const {
    licenses,
    totalCount,
    questionColumns,
    isLoading: isListLoading,
    isForegroundFetching: isListForegroundFetching,
    dataUpdatedAt,
    refetch: refetchLicenses,
  } = usePaginatedOmtAmsLicenses({
    page: pageIndex,
    pageSize,
    sortBy,
    sortDir,
    columnFilters,
    tab: listTab,
    bulkPaymentCohort,
    enabled: isAuthSetupComplete && listTab !== undefined,
  });

  const statsQuery = useQuery({
    queryKey: ["omt-ams-stats"],
    queryFn: () => getOmtAmsLicenseStats({}),
    enabled: isAuthSetupComplete,
  });

  const [mapViewport, setMapViewport] = useState<MapViewport | null>(null);
  const handleMapViewport = useCallback((viewport: MapViewport) => {
    setMapViewport(viewport);
  }, []);
  const {
    pins,
    clusters: mapClusters,
    totalInView: mapTotalInView,
    isFetching: isPinsFetching,
  } = useOmtAmsLicenseMapPins({
    columnFilters,
    tab: listTab,
    bulkPaymentCohort,
    enabled: isAuthSetupComplete && isMapView && listTab !== undefined,
    viewport: mapViewport,
  });

  const filterValuesRequestByColumnRef = useRef<Record<string, number>>({});
  const filterPopoverQueryRef = useRef({
    columnFilters,
    listTab,
    bulkPaymentCohort,
  });
  filterPopoverQueryRef.current = {
    columnFilters,
    listTab,
    bulkPaymentCohort,
  };
  const handleFilterPopoverOpen = useCallback(
    async (columnId: string, search?: string) => {
      const requestId =
        (filterValuesRequestByColumnRef.current[columnId] ?? 0) + 1;
      filterValuesRequestByColumnRef.current[columnId] = requestId;
      const q = filterPopoverQueryRef.current;
      try {
        const values = await fetchOmtAmsLicenseFilterValues({
          column: columnId,
          columnFilters: q.columnFilters,
          tab: q.listTab,
          bulkPaymentCohort: q.bulkPaymentCohort,
          search,
        });
        if (filterValuesRequestByColumnRef.current[columnId] !== requestId) {
          return;
        }
        setFacetValuesByColumn((prev) => {
          const existing = prev[columnId];
          if (
            existing &&
            existing.length === values.length &&
            existing.every((value, index) => value === values[index])
          ) {
            return prev;
          }
          return { ...prev, [columnId]: values };
        });
      } catch {
        if (filterValuesRequestByColumnRef.current[columnId] !== requestId) {
          return;
        }
        setFacetValuesByColumn((prev) => {
          if (prev[columnId]?.length === 0) return prev;
          return { ...prev, [columnId]: [] };
        });
      }
    },
    [],
  );

  const { licenseData, isPreviewLoading, isRuntimeRefreshing, previewError } =
    useLicenseDetailsWithRuntimeRefresh({
      licenseId: openLicenseId ?? "",
      enabled: Boolean(openLicenseId) && isAuthSetupComplete,
    });

  const commentsQuery = useQuery({
    queryKey: ["omt-ams-license-comments", openLicenseId],
    queryFn: () => getOmtAmsLicenseComments(openLicenseId ?? ""),
    enabled: Boolean(openLicenseId) && isAuthSetupComplete,
  });

  const paymentsQuery = useQuery({
    queryKey: ["omt-ams-license-payments", openLicenseId],
    queryFn: () => getOmtAmsLicensePayments(openLicenseId ?? ""),
    enabled: Boolean(openLicenseId) && isAuthSetupComplete,
  });

  const additionalQuestionsQuery = useQuery({
    ...getLicenseTypeQuestionsOptions({
      path: {
        license_type_id: licenseData?.license_type.id as string,
        jurisdiction_id: licenseData?.jurisdiction.id as string,
      },
    }),
    enabled:
      Boolean(openLicenseId) &&
      isAuthSetupComplete &&
      !isPreviewLoading &&
      !!licenseData,
  });

  if (!isAuthSetupComplete) {
    return <LoadingScreen message="Authenticating..." />;
  }
  if (accessQuery.isLoading) {
    return <LoadingScreen message="Checking access..." />;
  }
  if (accessQuery.error) {
    return (
      <ErrorComponent
        title={getQueryErrorHeading(accessQuery.error)}
        message={getQueryErrorMessage(accessQuery.error)}
      />
    );
  }

  const canManageUsers = Boolean(accessQuery.data?.can_manage_users);

  const licenseWorkspaceTabs = [
    "all",
    "needs-attention",
    "renewable",
    "inactive",
  ] as const;
  const showLicenseWorkspace = licenseWorkspaceTabs.includes(
    selectedTab as (typeof licenseWorkspaceTabs)[number],
  );
  // Title, tiles, and tabs live inside the list scroller so rows can run to
  // the top of the screen, matching the registrant license list. Non-list
  // tabs (Users) still host chrome inside their pane so it is not also
  // painted above the tab content.
  const listHostsPageChrome = true;

  const renderDashboardHeader = (stickyTitle = false) => (
    <DashboardHeader
      helpArticleId={getOmtAmsDashboardContextHelpArticleId(selectedTab)}
      helpPortal="omt-ams"
      showRegisterButton={false}
      hidePaymentActions
      hideBalanceDue
      stickyTitle={stickyTitle}
      needsAttentionCount={statsQuery.data?.needs_attention_count ?? 0}
      bulkPaymentPayLaterCount={statsQuery.data?.bulk_payment_count ?? 0}
      onViewNeedsAttentionRegistrations={() => {
        setBulkPaymentCohort(false);
        setSelectedTab("needs-attention");
      }}
      onViewBulkPaymentPayLaterRegistrations={() => {
        setBulkPaymentCohort(true);
        setSelectedTab("all");
      }}
      onSelectWorkspaceTab={(tab) => {
        if (tab !== "all") {
          setBulkPaymentCohort(false);
        }
        setSelectedTab(tab);
      }}
      registrantBulkPaymentPayLaterCohortActive={bulkPaymentCohort}
      onClearRegistrantBulkPaymentPayLaterCohort={() =>
        setBulkPaymentCohort(false)
      }
    />
  );

  const dashboardTabsBar = (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <DashboardTabList
        showUsersTab={canManageUsers}
        showUnregisteredTab={false}
        showPaymentsTab={false}
        showInspectionsTab={false}
        showInvoicesTab={false}
        showBalanceDueTab={false}
        needsAttentionCount={statsQuery.data?.needs_attention_count ?? 0}
      />
      {showLicenseWorkspace ? (
        <DashboardViewToggle
          isMapView={isMapView}
          onViewChange={setIsMapView}
        />
      ) : null}
    </div>
  );

  const renderPageChrome = () => (
    <div className="pb-4">
      {renderDashboardHeader(true)}
      {dashboardTabsBar}
    </div>
  );

  const hasColumnFilters = Object.values(columnFilters).some(
    (values) => values.length > 0,
  );
  const emptyListNote =
    !isListLoading && totalCount === 0 && !hasColumnFilters ? (
      <p className="text-sm text-muted-foreground">
        No properties list you as OMT or AMS
        {selectedTab !== "all" ? " in this tab" : ""}.
      </p>
    ) : null;

  const renderLicenseWorkspace = () => {
    if (isMapView) {
      return (
        <DashboardMapViewLayout
          leadingContent={renderPageChrome()}
          facets={null}
        >
          <MapErrorBoundary
            onSwitchToListView={() => setIsMapView(false)}
            className="h-full w-full"
          >
            <DashboardMap
              pins={pins}
              clusters={mapClusters}
              totalInView={mapTotalInView}
              isLoading={isPinsFetching}
              onViewportChange={handleMapViewport}
              onPinClick={(licenseId) => setOpenLicenseId(licenseId)}
              className="h-full min-h-0 w-full"
            />
          </MapErrorBoundary>
        </DashboardMapViewLayout>
      );
    }
    return (
      <RegistrantBulkRegistrationsTable
        licenses={licenses}
        questionColumns={questionColumns}
        showRegistrantColumn
        inactiveTab={selectedTab === "inactive"}
        hideRowActions
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        facetedValuesByColumn={facetValuesByColumn}
        onLicenseRowClick={(licenseId) => setOpenLicenseId(licenseId)}
        serverMode
        lockToViewport
        rowCount={totalCount}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPaginationChange={(next) => {
          setPageIndex(next.pageIndex);
          setPageSize(next.pageSize);
        }}
        sorting={sorting}
        onSortingChange={setSorting}
        onRefresh={() => {
          void refetchLicenses();
        }}
        isRefreshing={isListForegroundFetching}
        dataUpdatedAt={dataUpdatedAt}
        onFilterPopoverOpen={handleFilterPopoverOpen}
        leadingContent={
          <>
            {renderPageChrome()}
            {emptyListNote}
          </>
        }
      />
    );
  };

  return (
    <div className="flex min-h-0 w-full min-w-[375px] flex-1 flex-col gap-6 overflow-hidden">
      <Tabs
        value={selectedTab}
        onValueChange={(tab) => {
          if (tab !== "all") {
            setBulkPaymentCohort(false);
          }
          setSelectedTab(tab);
        }}
        className="flex min-h-0 min-w-0 flex-1 flex-col"
      >
        {listHostsPageChrome ? null : (
          <div className="space-y-4">
            {renderDashboardHeader()}
            {dashboardTabsBar}
          </div>
        )}

        {licenseWorkspaceTabs.map((tab) => (
          <TabsContent
            key={tab}
            value={tab}
            className={cn(
              "mt-0 flex min-h-0 flex-1 flex-col overflow-hidden data-[state=inactive]:hidden",
            )}
          >
            {renderLicenseWorkspace()}
          </TabsContent>
        ))}
        {canManageUsers ? (
          <TabsContent
            value="users"
            className="mt-0 flex min-h-0 flex-1 flex-col overflow-hidden data-[state=inactive]:hidden"
          >
            <OmtAmsDashboardUsers
              isAuthSetupComplete={isAuthSetupComplete}
              leadingContent={renderPageChrome()}
            />
          </TabsContent>
        ) : null}
      </Tabs>

      <LicenseDetailsSheet
        isOpen={Boolean(openLicenseId)}
        onOpenChange={(open) => {
          if (!open) {
            setOpenLicenseId(null);
            void navigate({
              to: "/omt-ams/dashboard",
              search: { tab: selectedTab },
            });
          }
        }}
        isSelectedLicenseLoading={isPreviewLoading}
        isAdditionalQuestionsQueryLoading={additionalQuestionsQuery.isLoading}
        selectedLicenseError={previewError}
        additionalQuestionsQueryError={additionalQuestionsQuery.error}
        additionalQuestions={additionalQuestionsQuery.data}
        selectedLicenseData={licenseData}
        commentsData={commentsQuery.data}
        isCommentsQueryLoading={commentsQuery.isLoading}
        commentsQueryError={commentsQuery.error}
        paymentsData={paymentsQuery.data}
        isPaymentsQueryLoading={paymentsQuery.isLoading}
        paymentsQueryError={paymentsQuery.error}
        isLicenseRuntimeRefreshing={isRuntimeRefreshing}
        userType={UserType.OMT_AMS}
      />
    </div>
  );
}
